import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Search,
  Puzzle,
  Star,
  Download,
  Filter,
  Grid3X3,
  ArrowRight,
  Server,
  Plus,
  Check,
  Image,
  Code,
  Globe,
  Languages,
  FileText,
  Cloud,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { showSuccess, showError } from '@/lib/toast';

// Icon mapping for skills - maps skill names to Lucide icons
const SKILL_ICON_MAP: Record<string, any> = {
  'image-generator': Image,
  'code-runner': Code,
  'web-search': Globe,
  translator: Languages,
  'file-manager': FileText,
  weather: Cloud,
  default: Zap,
};

// Get icon component for a skill
const getSkillIcon = (skillName: string, skillId: string): any => {
  const normalizedName = skillName.toLowerCase().replace(/\s+/g, '-');
  const normalizedId = skillId.toLowerCase();

  // Try exact match on name
  if (SKILL_ICON_MAP[normalizedName]) return SKILL_ICON_MAP[normalizedName];

  // Try exact match on ID
  if (SKILL_ICON_MAP[normalizedId]) return SKILL_ICON_MAP[normalizedId];

  // Try partial match on name
  for (const [key, icon] of Object.entries(SKILL_ICON_MAP)) {
    if (normalizedName.includes(key) || key.includes(normalizedName)) {
      return icon;
    }
  }

  return SKILL_ICON_MAP.default;
};

interface Skill {
  id: string;
  name: string;
  nameJa?: string;
  author: string;
  description: string;
  descriptionJa?: string;
  icon: string;
  category: string;
  version: string;
  downloads: number;
  rating: number;
}

const CATEGORIES = [
  { key: 'all', label: 'All', labelJa: 'すべて' },
  { key: 'productivity', label: 'Productivity', labelJa: '生産性' },
  { key: 'utility', label: 'Utility', labelJa: 'ユーティリティ' },
  { key: 'communication', label: 'Communication', labelJa: 'コミュニケーション' },
  { key: 'development', label: 'Development', labelJa: '開発' },
  { key: 'media', label: 'Media', labelJa: 'メディア' },
];

export function SkillBrowser() {
  const { t, i18n } = useTranslation();
  const isJa = i18n.language === 'ja';
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>('');

  // Fetch user's instances
  const { data: instancesData } = useQuery({
    queryKey: ['instances'],
    queryFn: async () => {
      const { data } = await api.get('/instances?limit=100');
      return data;
    },
  });

  const instances = (instancesData?.instances || []).filter(
    (i: any) => i.status === 'running' || i.status === 'starting',
  );

  const { data, isLoading } = useQuery({
    queryKey: ['skills', search, category],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (category !== 'all') params.set('category', category);
      params.set('page', '1');
      params.set('limit', '50');
      const { data } = await api.get(`/skills?${params.toString()}`);
      return data;
    },
  });

  const skills: Skill[] = data?.skills || [];

  const installMutation = useMutation({
    mutationFn: async ({ instanceId, skillId }: { instanceId: string; skillId: string }) => {
      const { data } = await api.post(`/instances/${instanceId}/skills/install`, { skillId });
      return data;
    },
    onSuccess: () => {
      showSuccess(t('skills.installSuccessMsg'));
      setSelectedSkill(null);
      setSelectedInstanceId('');
      queryClient.invalidateQueries({ queryKey: ['instance-skills'] });
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || t('skills.installErrorMsg'));
    },
  });

  const formatDownloads = (count: number): string => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
    return count.toString();
  };

  const handleInstall = () => {
    if (!selectedInstanceId || !selectedSkill) return;
    installMutation.mutate({ instanceId: selectedInstanceId, skillId: selectedSkill.id });
  };

  // No instances — redirect to create
  if (instancesData && instances.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Puzzle className="h-5 w-5 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {t('skills.title')}
            </h1>
          </div>
        </div>
        <Card className="border-dashed border-2 border-primary/20 bg-primary/5">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="h-20 w-20 rounded-full gradient-primary flex items-center justify-center mb-6">
              <Server className="h-10 w-10 text-white" />
            </div>
            <h3 className="text-xl font-bold mb-2">
              {t('skills.createInstanceFirst')}
            </h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-md text-center">
              {t('skills.createInstanceHint')}
            </p>
            <Link to="/dashboard/new">
              <Button size="lg" className="gap-2">
                <Plus className="h-4 w-4" />
                {t('skills.createInstance')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Puzzle className="h-5 w-5 text-emerald-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">
              {t('skills.title')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('skills.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('skills.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-11"
        />
      </div>

      {/* Category filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground mr-1" />
        {CATEGORIES.map((cat) => (
          <Button
            key={cat.key}
            variant={category === cat.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCategory(cat.key)}
            className={cn(
              'rounded-full transition-all',
              category === cat.key
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20'
                : 'hover:border-emerald-300 hover:text-emerald-600',
            )}
          >
            {t(`skills.categories.${cat.key}`)}
          </Button>
        ))}
      </div>

      <Separator />

      {/* Loading state */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-64 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : skills.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-20">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Grid3X3 className="h-8 w-8 text-muted-foreground/40" />
            </div>
            <h3 className="text-lg font-medium mb-2">
              {t('skills.noSkillsFound')}
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              {t('skills.noSkillsHint')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {skills.map((skill) => (
            <Card
              key={skill.id}
              className="group cursor-pointer transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/10 hover:border-emerald-300/50 dark:hover:border-emerald-700/50 hover:-translate-y-1 border-0 shadow-md bg-gradient-to-b from-white to-gray-50/50 dark:from-card dark:to-card/50"
              onClick={() => setSelectedSkill(skill)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start gap-3">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/25 flex-shrink-0 group-hover:scale-110 group-hover:shadow-xl group-hover:shadow-emerald-500/30 transition-all duration-300">
                    {(() => {
                      const IconComponent = getSkillIcon(skill.name, skill.id);
                      return <IconComponent className="h-6 w-6 text-white" />;
                    })()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-base font-bold leading-tight">
                      {isJa && skill.nameJa ? skill.nameJa : skill.name}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t('skills.by')} {skill.author}
                    </p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                  {isJa && skill.descriptionJa ? skill.descriptionJa : skill.description}
                </p>

                <div>
                  <Badge variant="secondary" className="capitalize text-[11px]">
                    {t(`skills.categories.${skill.category}`, skill.category)}
                  </Badge>
                </div>

                <Separator />

                <div className="flex items-center justify-between pt-1">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <Download className="h-3.5 w-3.5 text-blue-500" />
                      {formatDownloads(skill.downloads)}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                      {skill.rating.toFixed(1)}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs gap-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/50 px-3 h-8 font-medium group-hover:bg-emerald-50 dark:group-hover:bg-emerald-950/30 transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSkill(skill);
                    }}
                  >
                    {t('skills.viewDetails')}
                    <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Skill detail dialog with instance selector */}
      <Dialog open={!!selectedSkill} onOpenChange={(open) => { if (!open) { setSelectedSkill(null); setSelectedInstanceId(''); } }}>
        <DialogContent className="sm:max-w-lg">
          {selectedSkill && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4 mb-2">
                  <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-xl shadow-emerald-500/30 flex-shrink-0">
                    {(() => {
                      const IconComponent = getSkillIcon(selectedSkill.name, selectedSkill.id);
                      return <IconComponent className="h-7 w-7 text-white" />;
                    })()}
                  </div>
                  <div>
                    <DialogTitle className="text-xl">
                      {isJa && selectedSkill.nameJa ? selectedSkill.nameJa : selectedSkill.name}
                    </DialogTitle>
                    <DialogDescription className="mt-0.5">
                      v{selectedSkill.version} &middot; {t('skills.by')} {selectedSkill.author}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <Separator />

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-1.5">
                    {t('skills.description')}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {isJa && selectedSkill.descriptionJa
                      ? selectedSkill.descriptionJa
                      : selectedSkill.description}
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className="capitalize">
                    {t(`skills.categories.${selectedSkill.category}`, selectedSkill.category)}
                  </Badge>
                </div>

                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Download className="h-4 w-4" />
                    <span>
                      {formatDownloads(selectedSkill.downloads)} {t('skills.downloads')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                    <span>{selectedSkill.rating.toFixed(1)} / 5.0</span>
                  </div>
                </div>

                <Separator />

                {/* Instance selector */}
                <div>
                  <h4 className="text-sm font-medium mb-2">
                    {t('skills.selectInstance')}
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {instances.map((instance: any) => (
                      <button
                        key={instance.id}
                        onClick={() => setSelectedInstanceId(instance.id)}
                        className={cn(
                          'w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all',
                          selectedInstanceId === instance.id
                            ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                            : 'hover:bg-accent',
                        )}
                      >
                        <Server className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{instance.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{instance.hosting_type}</p>
                        </div>
                        {selectedInstanceId === instance.id && (
                          <Check className="h-4 w-4 text-primary flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/20"
                  onClick={handleInstall}
                  disabled={!selectedInstanceId || installMutation.isPending}
                >
                  <Download className="h-4 w-4" />
                  {installMutation.isPending
                    ? t('skills.installing')
                    : t('skills.installOnInstance')}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
